import { useEffect } from 'react';

import redirect from 'js/lib/coursera.redirect';
import { useParams } from 'js/lib/useRouter';

import DegreesExperiments from 'bundles/epic/clients/Degrees';
import constants from 'bundles/premium-certificate/constants/constants';
import {
  PRE_PROD_DEGREE_PREFIX,
  allExperimentDegreeSlugs,
} from 'bundles/program-loggedin-shared/libraries/dpmExperiments';

export const BOULDER_MS_CS_DEGREE_SLUG = 'ms-computer-science-boulder';

/** If the user has preprod access, allow access to the full set of degree slugs, otherwise filter out preprod___ degrees */
const filterExperimentDegreesBasedOnPreProdAccess = (degreeSlugs: string[]): string[] => {
  const dpmPreProdDegreesShouldBeShown = Boolean(DegreesExperiments.get('showDPMPreProdDegrees'));
  if (!dpmPreProdDegreesShouldBeShown) {
    return degreeSlugs.filter((slug) => !slug.startsWith(PRE_PROD_DEGREE_PREFIX));
  }

  return degreeSlugs.map((slug: string) => slug.replace(PRE_PROD_DEGREE_PREFIX, ''));
};

/**
 * Degrees enabled for Degree Progress Map
 */
const experimentDegreeSlugs = (): string[] => {
  const epicDegreeSlugs = allExperimentDegreeSlugs();

  return filterExperimentDegreesBasedOnPreProdAccess(epicDegreeSlugs);
};

export const degreeIsPartOfV3Experiment = (degreeSlug: string): boolean => experimentDegreeSlugs().includes(degreeSlug);

const isBoulderMSCSDegree = (degreeSlug: string): boolean => degreeSlug === BOULDER_MS_CS_DEGREE_SLUG;
const learnerIsPartOfV3ExperimentBoulderGroup = () => DegreesExperiments.get('boulderMSCSLearnerIsPartOfDPMExperiment');

const learnerIsPartOfV3ExperimentGroup = (degreeSlug: string): boolean => {
  if (isBoulderMSCSDegree(degreeSlug)) {
    return learnerIsPartOfV3ExperimentBoulderGroup();
  }
  // For non Boulder MS CS degrees
  return true;
};

export const degreeAndLearnerArePartOfV3ControlGroup = (degreeSlug: string) =>
  degreeIsPartOfV3Experiment(degreeSlug) && !learnerIsPartOfV3ExperimentGroup(degreeSlug);

/** Whether the DPM / Degree V3 experience should be displayed to the learner */
export const degreeV3ShouldBeShown = (degreeSlug: string): boolean => {
  // Initially filter by set of released degrees
  if (!degreeIsPartOfV3Experiment(degreeSlug)) {
    return false;
  }

  // Only fire an experiment get for a student if they're part of the degree so we don't have a massive group that can't see either experience
  return learnerIsPartOfV3ExperimentGroup(degreeSlug);
};

/** Whether the DPM / Degree V3 event UX should be displayed to the learner */
export const degreeV3EventShouldBeShown = (): boolean => {
  return Boolean(DegreesExperiments.get('showDPMEventUX'));
};

export const pbaCurriculumShouldBeShown = (): boolean => {
  return Boolean(DegreesExperiments.get('showPBACurriculum'));
};

export type toOrFrom = 'to' | 'from';
/** @private */
export const useV3Redirector = (redirectDirection: toOrFrom, degreeAppPath: string) => {
  const { degreeSlug } = useParams();
  useEffect(() => {
    if (!degreeSlug) {
      return;
    }

    const inV3Mode = degreeV3ShouldBeShown(degreeSlug);
    const conditionsForRedirection =
      (!inV3Mode && redirectDirection === 'from') || (inV3Mode && redirectDirection === 'to');

    if (conditionsForRedirection) {
      const basePath = `/${constants.degreeUrlRoot}/${degreeSlug}/`;
      redirect.setLocation(`${basePath}${degreeAppPath}`);
    }
  }, [degreeAppPath, degreeSlug, redirectDirection]);
};

/** Redirect to an appropriate location if the user **is** part of the DPM / Degree V3 experiment */
export const useRedirectIfV3 = (degreeAppPath: string) => useV3Redirector('to', degreeAppPath);

/** Redirect to an appropriate location if the user **is not** part of the DPM / Degree V3 experiment */
export const useRedirectIfNotV3 = (degreeAppPath: string) => useV3Redirector('from', degreeAppPath);
